import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import moment from "moment";
import { buildTrackerDashboardStats } from "./trackerDashboardStats";
import {
  getInitials,
  getSubmissionReviewLabel,
  getSubmissionSourceLabel,
  loadSubmissionsFromApi,
  SUBMISSION_REVIEW,
  SUBMISSION_SOURCES,
} from "./submissionStorage";
import { normalizeTracker } from "./trackerStorage";
import { getTrackersApi } from "../../DAL/tracker/tracker";
import { coachAvatarColor } from "./AddCoachesPopup";
import Label from "src/components/Label";

const PRIMARY = "var(--portal-theme-primary, #04abbb)";
const PRIMARY_SOFT =
  "color-mix(in srgb, var(--portal-theme-primary) 12%, white)";
const BORDER = "#e2e8f0";
const MUTED = "#64748b";
const INK = "#0f172a";

const ACTIVITY_META = {
  active: { label: "Active", color: "#047857", bg: "rgba(16,185,129,0.12)" },
  cooling: { label: "Cooling", color: "#b45309", bg: "rgba(245,158,11,0.14)" },
  at_risk: { label: "Quiet", color: "#b91c1c", bg: "rgba(239,68,68,0.12)" },
  completed: {
    label: "Completed",
    color: "#0369a1",
    bg: "rgba(14,165,233,0.12)",
  },
  dropped: { label: "Dropped", color: "#6b7280", bg: "rgba(148,163,184,0.2)" },
  left: { label: "Left", color: "#475569", bg: "rgba(100,116,139,0.16)" },
  unknown: { label: "—", color: MUTED, bg: "#f1f5f9" },
};

const VERDICT_META = {
  up: {
    label: "Improving",
    color: "#047857",
    bg: "rgba(16,185,129,0.12)",
    Icon: TrendingUpIcon,
  },
  down: {
    label: "Declining",
    color: "#b91c1c",
    bg: "rgba(239,68,68,0.12)",
    Icon: TrendingDownIcon,
  },
  mixed: {
    label: "Mixed",
    color: "#b45309",
    bg: "rgba(245,158,11,0.14)",
    Icon: TrendingFlatIcon,
  },
  flat: {
    label: "Stable",
    color: MUTED,
    bg: "#f1f5f9",
    Icon: TrendingFlatIcon,
  },
  none: {
    label: "—",
    color: MUTED,
    bg: "#f1f5f9",
    Icon: TrendingFlatIcon,
  },
};

const RankBadge = ({ rank }) => (
  <Box
    sx={{
      width: 22,
      height: 22,
      borderRadius: "7px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 11,
      fontWeight: 800,
      bgcolor: rank <= 3 ? "rgba(4,171,187,0.12)" : "#f1f5f9",
      color: rank <= 3 ? "#0f766e" : MUTED,
      flexShrink: 0,
    }}
  >
    {rank}
  </Box>
);

const StatusDotBadge = ({ label, color, bg }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.55,
      px: 0.85,
      py: 0.25,
      borderRadius: "999px",
      bgcolor: bg,
      color,
      flexShrink: 0,
    }}
  >
    <Box
      sx={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        bgcolor: color,
        flexShrink: 0,
      }}
    />
    <Typography
      sx={{
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: 0.35,
        lineHeight: 1.2,
      }}
    >
      {label}
    </Typography>
  </Box>
);

const personOffTrackMeta = (c) => {
  const days = c.daysSince || 0;
  const journey =
    Array.isArray(c.trackers) && c.trackers.length ? c.trackers[0] : "Journey";
  const coachName =
    c.coachName && c.coachName !== "—" ? c.coachName : "Unassigned";
  const primary = c.performance?.primary;
  const pending = c.pendingReviews || 0;
  const overdue = c.overdueReviews || 0;
  const submissions = c.submissions || 0;
  const progress = c.progress || 0;
  const offTrack =
    c.performance?.verdict === "down" || overdue > 0 || days >= 21;

  const issues = [];
  if (overdue > 0) {
    issues.push(
      `${overdue} coach review${overdue === 1 ? "" : "s"} overdue${
        days ? ` — last check-in ${days} d ago` : ""
      }`
    );
  }
  if (pending > 0) {
    issues.push(
      `${pending} review${pending === 1 ? "" : "s"} still pending with coach`
    );
  }
  if (days >= 7) {
    issues.push(
      `No check-in for ${days} day${days === 1 ? "" : "s"}`
    );
  }
  if (c.performance?.verdict === "down" && primary) {
    issues.push(
      `Off track on ${primary.label}: ${primary.currentText} (${primary.deltaText})`
    );
  } else if (c.performance?.verdict === "mixed" && primary) {
    issues.push(
      `Mixed progress on ${primary.label}: ${primary.currentText}`
    );
  }
  if (!issues.length) {
    issues.push(`${submissions} check-ins · ${progress}% journey complete`);
  }

  return {
    offTrack,
    badge: offTrack
      ? {
          label: "OFF TRACK",
          color: "#b91c1c",
          bg: "rgba(239,68,68,0.12)",
        }
      : {
          label: "NEEDS ATTENTION",
          color: "#b45309",
          bg: "rgba(245,158,11,0.14)",
        },
    statsLine: `${progress}% journey · ${submissions} entries · ${pending} pending · ${overdue} overdue · coach ${coachName} · ${journey}`,
    issues,
    severity:
      overdue * 40 +
      (c.performance?.verdict === "down" ? 30 : 0) +
      days +
      pending * 5,
  };
};

const OffTrackPersonRow = ({ person: c, onOpen }) => {
  const meta = personOffTrackMeta(c);
  return (
    <Box
      component="button"
      type="button"
      onClick={() => onOpen(c)}
      sx={{
        textAlign: "left",
        width: "100%",
        border: 0,
        borderBottom: `1px solid ${BORDER}`,
        borderRadius: 0,
        bgcolor: "transparent",
        px: 0.1,
        py: 1.25,
        cursor: "pointer",
        display: "flex",
        alignItems: "flex-start",
        gap: 1.1,
        transition: "background 0.15s",
        "&:last-of-type": { borderBottom: 0 },
        "&:hover": { bgcolor: "rgba(248,250,252,0.9)" },
      }}
    >
      <Avatar
        sx={{
          width: 36,
          height: 36,
          fontSize: 12,
          fontWeight: 700,
          bgcolor:
            c.source === SUBMISSION_SOURCES.PUBLIC ? "#8b5cf6" : "#04abbb",
          flexShrink: 0,
          mt: 0.15,
        }}
      >
        {getInitials(c.name)}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.85,
            flexWrap: "wrap",
            mb: 0.3,
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: INK,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {c.name}
          </Typography>
          <StatusDotBadge {...meta.badge} />
        </Box>
        <Typography
          sx={{
            fontSize: 12.5,
            color: MUTED,
            lineHeight: 1.4,
          }}
        >
          {meta.statsLine}
        </Typography>
        {meta.issues.length > 0 ? (
          <Box
            component="ul"
            sx={{
              m: 0,
              mt: 0.65,
              pl: 2,
              listStyle: "disc",
              "& li::marker": { color: "#ef4444" },
            }}
          >
            {meta.issues.slice(0, 3).map((issue) => (
              <Typography
                key={issue}
                component="li"
                sx={{
                  fontSize: 12,
                  color: MUTED,
                  lineHeight: 1.4,
                  mb: 0.25,
                  "&:last-of-type": { mb: 0 },
                }}
              >
                {issue}
              </Typography>
            ))}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

const PersonRow = ({ person: c, rank, onOpen, variant = "best" }) => {
  const activity = ACTIVITY_META[c.activity] || ACTIVITY_META.unknown;
  const verdict = VERDICT_META[c.performance?.verdict] || VERDICT_META.none;
  const VerdictIcon = verdict.Icon;
  const primary = c.performance?.primary;
  const quietTone = variant === "quiet";
  const leaderboardTone = variant === "leaderboard";
  const journey =
    Array.isArray(c.trackers) && c.trackers.length
      ? c.trackers[0]
      : "";
  const trackerCount = c.trackerCount ?? (c.trackers?.length || 0);
  const activeSubs = c.activeSubmissions ?? 0;
  const onTimeCount = c.onTime ?? 0;

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onOpen(c)}
      sx={{
        textAlign: "left",
        width: "100%",
        border: 0,
        borderBottom: `1px solid ${BORDER}`,
        borderRadius: 0,
        bgcolor: "transparent",
        px: 0.25,
        py: 1.15,
        cursor: "pointer",
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        transition: "background 0.15s",
        "&:last-of-type": { borderBottom: 0 },
        "&:hover": {
          bgcolor: quietTone ? "rgba(245,158,11,0.06)" : PRIMARY_SOFT,
        },
      }}
    >
      <RankBadge rank={rank} />
      <Avatar
        sx={{
          width: 34,
          height: 34,
          fontSize: 11,
          fontWeight: 700,
          bgcolor:
            c.source === SUBMISSION_SOURCES.PUBLIC ? "#8b5cf6" : "#04abbb",
          flexShrink: 0,
        }}
      >
        {getInitials(c.name)}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.75,
          }}
        >
          <Typography
            sx={{
              fontSize: 13.5,
              fontWeight: 700,
              color: INK,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {c.name}
          </Typography>
          {quietTone ? (
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 800,
                color: c.daysSince >= 21 ? "#b91c1c" : "#b45309",
                flexShrink: 0,
              }}
            >
              {c.daysSince}d quiet
            </Typography>
          ) : leaderboardTone ? (
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 800,
                color: "#0f766e",
                flexShrink: 0,
              }}
            >
              {onTimeCount} on time
            </Typography>
          ) : (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.3,
                px: 0.7,
                py: 0.2,
                borderRadius: "999px",
                bgcolor: verdict.bg,
                color: verdict.color,
                flexShrink: 0,
              }}
            >
              <VerdictIcon sx={{ fontSize: 13 }} />
              <Typography sx={{ fontSize: 10.5, fontWeight: 800 }}>
                {verdict.label}
              </Typography>
            </Box>
          )}
        </Box>
        <Typography sx={{ fontSize: 11.5, color: MUTED, mt: 0.2 }}>
          {leaderboardTone
            ? `${trackerCount} tracker${trackerCount === 1 ? "" : "s"}${
                c.coachName ? ` · ${c.coachName}` : ""
              }`
            : `${journey || "Journey"}${c.coachName ? ` · ${c.coachName}` : ""}`}
        </Typography>
        <Typography
          sx={{
            mt: 0.35,
            fontSize: 12,
            fontWeight: 700,
            color: quietTone
              ? activity.color
              : leaderboardTone
                ? INK
                : verdict.color,
          }}
        >
          {quietTone
            ? `${activity.label} · last ${
                c.lastAt ? moment(c.lastAt).fromNow() : "—"
              }`
            : leaderboardTone
              ? `${activeSubs} active · ${onTimeCount} on time · ${
                  c.submissions || 0
                } total`
              : primary
                ? `${primary.label}: ${primary.currentText} (${primary.deltaText})`
                : `${c.submissions} check-ins · ${c.progress}%`}
        </Typography>
      </Box>
    </Box>
  );
};

const StatCard = ({ label, value, hint, tone, onClick }) => (
  <Box
    component={onClick ? "button" : "div"}
    type={onClick ? "button" : undefined}
    onClick={onClick}
    sx={{
      flex: "1 1 160px",
      minWidth: 150,
      p: 1.75,
      borderRadius: "12px",
      border: `1px solid ${BORDER}`,
      borderLeft: tone ? `3px solid ${tone}` : `1px solid ${BORDER}`,
      bgcolor: "#fff",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      textAlign: "left",
      cursor: onClick ? "pointer" : "default",
      transition: "border-color 0.15s ease, box-shadow 0.15s ease",
      "&:hover": onClick
        ? {
            borderColor: PRIMARY,
            boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
          }
        : undefined,
    }}
  >
    <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: MUTED }}>
      {label}
    </Typography>
    <Typography
      sx={{
        mt: 0.5,
        fontSize: 24,
        fontWeight: 700,
        color: tone || INK,
        lineHeight: 1.15,
      }}
    >
      {value}
    </Typography>
    {hint ? (
      <Typography sx={{ mt: 0.5, fontSize: 11.5, color: MUTED, lineHeight: 1.35 }}>
        {hint}
      </Typography>
    ) : null}
  </Box>
);

const Panel = ({ title, subtitle, action, children, sx }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: "12px",
      border: `1px solid ${BORDER}`,
      bgcolor: "#fff",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      ...sx,
    }}
  >
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      spacing={1}
      sx={{ mb: 1.5, flexShrink: 0 }}
    >
      <Box>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: INK }}>
          {title}
        </Typography>
        {/* {subtitle ? (
          <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.25 }}>
            {subtitle}
          </Typography>
        ) : null} */}
      </Box>
      {action}
    </Stack>
    {children}
  </Box>
);

const reviewColor = (status) => {
  if (status === SUBMISSION_REVIEW.OVERDUE) return "error";
  if (status === SUBMISSION_REVIEW.PENDING) return "warning";
  if (status === SUBMISSION_REVIEW.REVIEWED) return "success";
  return "default";
};

/**
 * Main dashboard tracker block — KPIs, coach table, new submissions.
 * No tabs / heavy charts.
 */
export default function TrackerAccountabilitySection() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [trackers, setTrackers] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [trackersRes, submissionsRes] = await Promise.all([
          getTrackersApi({ limit: 500 }),
          loadSubmissionsFromApi(null, { limit: 15 }),
        ]);
        if (cancelled) return;
        const nextTrackers =
          trackersRes?.code === 200 && Array.isArray(trackersRes.trackers)
            ? trackersRes.trackers.map(normalizeTracker).filter(Boolean)
            : [];
        setTrackers(nextTrackers);
        setSubmissions(
          submissionsRes?.ok && Array.isArray(submissionsRes.rows)
            ? submissionsRes.rows
            : []
        );
        if (trackersRes?.code !== 200) {
          enqueueSnackbar(
            trackersRes?.message || "Failed to load tracker stats",
            { variant: "warning" }
          );
        }
        if (submissionsRes && !submissionsRes.ok) {
          enqueueSnackbar(
            submissionsRes.message || "Failed to load submission stats",
            { variant: "warning" }
          );
        }
      } catch {
        if (!cancelled) {
          setTrackers([]);
          setSubmissions([]);
          enqueueSnackbar("Failed to load tracker dashboard stats", {
            variant: "error",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enqueueSnackbar]);

  const stats = useMemo(
    () =>
      buildTrackerDashboardStats({
        trackers,
        submissions,
        trendDays: 14,
      }),
    [trackers, submissions]
  );
  const {
    kpis,
    coaches,
    clients,
    recentSubmissions,
    trackerPerf,
    activityMix,
  } = stats;

  const coachRows = coaches.filter((c) => c.id !== "unassigned");
  const trackerRows = trackerPerf || [];
  const people = clients || [];
  const hasData = kpis.submissions > 0 || kpis.trackers > 0;

  const reviewQueueCoaches = useMemo(() => {
    return [...coachRows]
      .filter(
        (c) =>
          (c.issues && c.issues.length > 0) ||
          (c.pending || 0) > 0 ||
          (c.overdue || 0) > 0 ||
          (c.quiet || 0) > 0 ||
          (c.offTrack || 0) > 0
      )
      .sort(
        (a, b) =>
          (a.grade ?? 100) - (b.grade ?? 100) ||
          (b.overdue || 0) - (a.overdue || 0) ||
          (b.reviewsDue || 0) - (a.reviewsDue || 0)
      )
      .slice(0, 6);
  }, [coachRows]);

  const notOnTrackPeople = useMemo(() => {
    const flagged = [...people]
      .filter(
        (c) =>
          !c.finished &&
          !c.dropped &&
          !c.left &&
          (c.activity === "at_risk" ||
            c.activity === "cooling" ||
            c.daysSince >= 5 ||
            (c.overdueReviews || 0) > 0 ||
            (c.pendingReviews || 0) > 0 ||
            c.performance?.verdict === "down" ||
            c.performance?.verdict === "mixed" ||
            (typeof c.progress === "number" && c.progress < 40))
      )
      .sort(
        (a, b) =>
          personOffTrackMeta(b).severity - personOffTrackMeta(a).severity ||
          (b.daysSince || 0) - (a.daysSince || 0)
      );
    if (flagged.length >= 6) return flagged;
    const flaggedKeys = new Set(flagged.map((c) => c.key));
    const fillers = [...people]
      .filter(
        (c) => !c.finished && !c.dropped && !c.left && !flaggedKeys.has(c.key)
      )
      .sort(
        (a, b) =>
          (b.daysSince || 0) - (a.daysSince || 0) ||
          (a.progress || 0) - (b.progress || 0)
      );
    return [...flagged, ...fillers];
  }, [people]);

  const quietPeople = useMemo(
    () => notOnTrackPeople.slice(0, 6),
    [notOnTrackPeople]
  );

  const bestPeople = useMemo(() => {
    const eligible = people.filter(
      (c) =>
        c.activity !== "dropped" &&
        c.activity !== "left" &&
        (c.performance?.verdict === "up" ||
          c.performance?.verdict === "mixed" ||
          c.progress >= 40 ||
          c.submissions >= 2 ||
          (c.onTime || 0) >= 2)
    );
    const pool = eligible.length
      ? eligible
      : people.filter((c) => !c.dropped && !c.left);
    return [...pool]
      .sort((a, b) => {
        const rank = (c) => {
          const v = c.performance?.verdict;
          const vScore =
            v === "up" ? 50 : v === "mixed" ? 25 : v === "flat" ? 10 : 0;
          return (
            vScore +
            (c.onTime || 0) * 4 +
            (c.activeSubmissions || 0) * 3 +
            (c.trackerCount || c.trackers?.length || 0) * 2 +
            (c.progress || 0) / 4 +
            (c.submissions || 0) -
            (c.daysSince || 0)
          );
        };
        return rank(b) - rank(a);
      })
      .slice(0, 6);
  }, [people]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 8,
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  const openPerson = (c) => {
    if (c.latestSubmissionId) {
      navigate(`/tracker-submissions/${c.latestSubmissionId}`);
      return;
    }
    navigate("/tracker-submissions");
  };

  return (
    <Box sx={{ mt: 1, mb: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#04abbb",
              fontSize: { xs: 18, md: 20 },
            }}
          >
            Tracker accountability
          </Typography>
          <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.25 }}>
            Who is performing, who went quiet, and how coaches are keeping up.
          </Typography>
        </Box>
        {/* <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label="View trackers"
            onClick={() => navigate("/trackers")}
            sx={{
              fontWeight: 600,
              bgcolor: "color-mix(in srgb, #04abbb 12%, white)",
              color: "#0f766e",
              cursor: "pointer",
            }}
          />
          <Chip
            size="small"
            label="All submissions"
            onClick={() => navigate("/tracker-submissions")}
            sx={{
              fontWeight: 600,
              border: `1px solid ${BORDER}`,
              bgcolor: "#fff",
              cursor: "pointer",
            }}
          />
        </Stack> */}
      </Stack>

      {!hasData ? (
        <Panel title="No tracker activity yet">
          <Typography sx={{ py: 3, textAlign: "center", color: MUTED, fontSize: 13 }}>
            Create a tracker to see accountability here.
          </Typography>
        </Panel>
      ) : (
        <>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mb: 2 }}>
            <StatCard
              label="People"
              value={kpis.people}
              hint={`${kpis.activeClients || 0} active`}
              tone={PRIMARY}
              onClick={() => navigate("/tracker-submissions")}
            />
            <StatCard
              label="Improving"
              value={kpis.improvingPeople || 0}
              hint="vs baseline"
              tone="#047857"
            />
            <StatCard
              label="Declining"
              value={kpis.decliningPeople || 0}
              hint="needs focus"
              tone={kpis.decliningPeople > 0 ? "#b91c1c" : MUTED}
            />
            <StatCard
              label="Quiet"
              value={(activityMix?.cooling || 0) + (activityMix?.at_risk || 0)}
              hint="7+ days without check-in"
              tone={
                (activityMix?.cooling || 0) + (activityMix?.at_risk || 0) > 0
                  ? "#b45309"
                  : MUTED
              }
            />
            <StatCard
              label="Reviews overdue"
              value={kpis.reviewsOverdue}
              hint="Past due"
              tone={kpis.reviewsOverdue > 0 ? "#b91c1c" : MUTED}
              onClick={() => navigate("/tracker-submissions")}
            />
            <StatCard
              label="Live trackers"
              value={kpis.live}
              hint={`${kpis.submissions} entries`}
              onClick={() => navigate("/trackers")}
            />
          </Box>

          <div className="row mb-2 align-items-stretch">
            <div className="col-lg-6 mt-2 d-flex">
              <Panel
                title="Coaches with review queue"
                subtitle="A grade from what they owe: reviews, check-ins, silent people, people off track."
                action={
                  <Typography
                    component="button"
                    type="button"
                    onClick={() => navigate("/tracker-submissions")}
                    sx={{
                      border: 0,
                      bgcolor: "transparent",
                      color: PRIMARY,
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                      p: 0,
                      lineHeight: 1.2,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Detail
                  </Typography>
                }
                sx={{
                  width: "100%",
                  height: 420,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {reviewQueueCoaches.length === 0 ? (
                  <Typography
                    sx={{ py: 2, textAlign: "center", color: MUTED, fontSize: 13 }}
                  >
                    Coaches are clear — nothing needs attention right now.
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: "auto",
                      pr: 0.5,
                    }}
                  >
                    {reviewQueueCoaches.map((c) => {
                      const health = c.health || "watch";
                      const badge =
                        health === "needs_attention"
                          ? {
                              label: "NEEDS ATTENTION",
                              color: "#b91c1c",
                              bg: "rgba(239,68,68,0.12)",
                            }
                          : health === "healthy"
                            ? {
                                label: "ON TRACK",
                                color: "#047857",
                                bg: "rgba(16,185,129,0.12)",
                              }
                            : {
                                label: "WATCH",
                                color: "#b45309",
                                bg: "rgba(245,158,11,0.14)",
                              };
                      const issues = Array.isArray(c.issues) ? c.issues : [];
                      const pending = c.pending || 0;
                      const overdue = c.overdue || 0;
                      const reviewed = c.reviewed || 0;
                      const people = c.people || 0;
                      const entries = c.submissions || 0;
                      const reviewRate = c.reviewRate || 0;
                      return (
                        <Box
                          key={c.id}
                          component="button"
                          type="button"
                          onClick={() => navigate("/tracker-submissions")}
                          sx={{
                            textAlign: "left",
                            width: "100%",
                            border: 0,
                            borderBottom: `1px solid ${BORDER}`,
                            borderRadius: 0,
                            bgcolor: "transparent",
                            px: 0.1,
                            py: 1.25,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.1,
                            transition: "background 0.15s",
                            "&:last-of-type": { borderBottom: 0 },
                            "&:hover": { bgcolor: "rgba(248,250,252,0.9)" },
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 36,
                              height: 36,
                              fontSize: 12,
                              fontWeight: 700,
                              bgcolor: coachAvatarColor({
                                id: c.id,
                                first_name: c.name,
                              }),
                              flexShrink: 0,
                              mt: 0.15,
                            }}
                          >
                            {getInitials(c.name)}
                          </Avatar>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.85,
                                flexWrap: "wrap",
                                mb: 0.3,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 14,
                                  fontWeight: 700,
                                  color: INK,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {c.name}
                              </Typography>
                              <StatusDotBadge {...badge} />
                            </Box>
                            <Typography
                              sx={{
                                fontSize: 12.5,
                                color: MUTED,
                                lineHeight: 1.4,
                              }}
                            >
                              {c.grade ?? 0}/100 · {people} people
                              {typeof c.onTrack === "number"
                                ? `, ${c.onTrack} on track`
                                : ""}
                              {" · "}
                              {pending} pending · {overdue} overdue ·{" "}
                              {reviewed} reviewed · {entries} entries ·{" "}
                              {reviewRate}%
                            </Typography>
                            {issues.length > 0 ? (
                              <Box
                                component="ul"
                                sx={{
                                  m: 0,
                                  mt: 0.65,
                                  pl: 2,
                                  listStyle: "disc",
                                  "& li::marker": { color: "#ef4444" },
                                }}
                              >
                                {issues.slice(0, 3).map((issue) => (
                                  <Typography
                                    key={issue}
                                    component="li"
                                    sx={{
                                      fontSize: 12,
                                      color: MUTED,
                                      lineHeight: 1.4,
                                      mb: 0.25,
                                      "&:last-of-type": { mb: 0 },
                                    }}
                                  >
                                    {issue}
                                  </Typography>
                                ))}
                              </Box>
                            ) : null}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Panel>
            </div>
            <div className="col-lg-6 mt-2 d-flex">
              <Panel
                title="People who are not on track"
                subtitle="By name, worst first, with the reason and the coach who owns it."
                action={
                  <Typography
                    component="button"
                    type="button"
                    onClick={() => navigate("/tracker-submissions")}
                    sx={{
                      border: 0,
                      bgcolor: "transparent",
                      color: PRIMARY,
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                      p: 0,
                      lineHeight: 1.2,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    All {notOnTrackPeople.length}
                  </Typography>
                }
                sx={{
                  width: "100%",
                  height: 420,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {quietPeople.length === 0 ? (
                  <Typography
                    sx={{ py: 3, textAlign: "center", color: MUTED, fontSize: 13 }}
                  >
                    Looking good — everyone is on track.
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: "auto",
                      pr: 0.5,
                    }}
                  >
                    {quietPeople.map((c) => (
                      <OffTrackPersonRow
                        key={c.key}
                        person={c}
                        onOpen={openPerson}
                      />
                    ))}
                  </Box>
                )}
              </Panel>
            </div>
          </div>

          <Panel
            title="Tracker performance"
            subtitle="Which journeys are doing well — people, completion, reviews"
            action={
              <Typography
                component="button"
                type="button"
                onClick={() => navigate("/trackers")}
                sx={{
                  border: 0,
                  bgcolor: "transparent",
                  color: "#04abbb",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  p: 0,
                }}
              >
                All trackers
              </Typography>
            }
          >
            {trackerRows.length === 0 ? (
              <Typography sx={{ py: 3, textAlign: "center", color: MUTED, fontSize: 13 }}>
                No trackers yet.
              </Typography>
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: MUTED, fontSize: 11 }}>
                        Tracker
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontWeight: 700, color: MUTED, fontSize: 11 }}
                      >
                        People
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontWeight: 700, color: MUTED, fontSize: 11 }}
                      >
                        Submissions
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontWeight: 700, color: MUTED, fontSize: 11 }}
                      >
                        Completed
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, color: MUTED, fontSize: 11 }}
                      >
                        Completion
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontWeight: 700, color: MUTED, fontSize: 11 }}
                      >
                        Reviews due
                      </TableCell>
                      {/* <TableCell
                        align="center"
                        sx={{ fontWeight: 700, color: MUTED, fontSize: 11 }}
                      >
                        Quiet
                      </TableCell> */}
                      <TableCell
                        align="center"
                        sx={{ fontWeight: 700, color: MUTED, fontSize: 11 }}
                      >
                        Coaches
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trackerRows.map((t) => (
                      <TableRow
                        key={t.id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => navigate(`/trackers/${t.id}`)}
                      >
                        <TableCell>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                            {t.name}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: MUTED }}>
                            {t.live ? "Live" : t.status || "Draft"}
                            {typeof t.avgProgress === "number"
                              ? ` · avg progress ${t.avgProgress}%`
                              : ""}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                          {t.people}
                        </TableCell>
                        <TableCell align="center">{t.submissions}</TableCell>
                        <TableCell
                          align="center"
                          sx={{ color: "#047857", fontWeight: 700 }}
                        >
                          {t.completed}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={`${t.completionRate}%`}
                            sx={{
                              height: 22,
                              fontWeight: 700,
                              fontSize: 11,
                              bgcolor:
                                t.completionRate >= 50
                                  ? "rgba(16,185,129,0.12)"
                                  : t.completionRate >= 25
                                    ? "rgba(245,158,11,0.14)"
                                    : "rgba(148,163,184,0.2)",
                              color:
                                t.completionRate >= 50
                                  ? "#047857"
                                  : t.completionRate >= 25
                                    ? "#b45309"
                                    : MUTED,
                            }}
                          />
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            fontWeight: 700,
                            color: t.reviewsDue > 0 ? "#b45309" : INK,
                          }}
                        >
                          {t.reviewsDue}
                        </TableCell>
                        {/* <TableCell
                          align="center"
                          sx={{
                            fontWeight: 700,
                            color: t.atRisk > 0 ? "#b91c1c" : INK,
                          }}
                        >
                          {t.atRisk}
                        </TableCell> */}
                        <TableCell align="center">{t.coaches}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Panel>

          <div className="row mb-2 align-items-stretch">
            <div className="col-lg-6 mt-2 d-flex">
              <Panel
                title="Coach performance"
                subtitle="Trackers, reviews completed / due / overdue"
                sx={{
                  width: "100%",
                  height: 360,
                  display: "flex",
                  flexDirection: "column",
                }}
                action={
                  <Typography
                    component="button"
                    type="button"
                    onClick={() => navigate("/tracker-submissions")}
                    sx={{
                      border: 0,
                      bgcolor: "transparent",
                      color: "#04abbb",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                      p: 0,
                    }}
                  >
                    Open submissions
                  </Typography>
                }
              >
                {coachRows.length === 0 ? (
                  <Typography
                    sx={{ py: 3, textAlign: "center", color: MUTED, fontSize: 13 }}
                  >
                    No coaches assigned on trackers yet.
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflow: "auto",
                      pr: 0.5,
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{ fontWeight: 700, color: MUTED, fontSize: 11 }}
                          >
                            Coach
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontWeight: 700, color: MUTED, fontSize: 11 }}
                          >
                            Trackers
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontWeight: 700, color: MUTED, fontSize: 11 }}
                          >
                            People
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontWeight: 700, color: MUTED, fontSize: 11 }}
                          >
                            Done
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontWeight: 700, color: MUTED, fontSize: 11 }}
                          >
                            Due
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontWeight: 700, color: MUTED, fontSize: 11 }}
                          >
                            Overdue
                          </TableCell>
                          {/* <TableCell
                            align="center"
                            sx={{ fontWeight: 700, color: MUTED, fontSize: 11 }}
                          >
                            %
                          </TableCell> */}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {coachRows.map((c) => (
                          <TableRow key={c.id} hover>
                            <TableCell>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Avatar
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    bgcolor: coachAvatarColor({
                                      id: c.id,
                                      first_name: c.name,
                                    }),
                                  }}
                                >
                                  {getInitials(c.name)}
                                </Avatar>
                                <Typography
                                  sx={{
                                    fontSize: 13,
                                    fontWeight: 600,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    maxWidth: 110,
                                  }}
                                >
                                  {c.name}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700 }}>
                              {c.trackers}
                            </TableCell>
                            <TableCell align="center">{c.people}</TableCell>
                            <TableCell
                              align="center"
                              sx={{ color: "#047857", fontWeight: 700 }}
                            >
                              {c.reviewed}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: 700,
                                color: c.pending > 0 ? "#b45309" : INK,
                              }}
                            >
                              {c.pending}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: 700,
                                color: c.overdue > 0 ? "#b91c1c" : INK,
                              }}
                            >
                              {c.overdue}
                            </TableCell>
                            {/* <TableCell align="center">
                              <Chip
                                size="small"
                                label={`${c.reviewRate}%`}
                                sx={{
                                  height: 22,
                                  fontWeight: 700,
                                  fontSize: 11,
                                  bgcolor:
                                    c.reviewRate >= 70
                                      ? "rgba(16,185,129,0.12)"
                                      : c.reviewRate >= 40
                                        ? "rgba(245,158,11,0.14)"
                                        : "rgba(239,68,68,0.12)",
                                  color:
                                    c.reviewRate >= 70
                                      ? "#047857"
                                      : c.reviewRate >= 40
                                        ? "#b45309"
                                        : "#b91c1c",
                                }}
                              />
                            </TableCell> */}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Panel>
            </div>

            <div className="col-lg-6 mt-2 d-flex">
              <Panel
                title="People leaderboard"
                subtitle="Top 6 best performers — trackers, active & on time"
                sx={{
                  width: "100%",
                  height: 360,
                  display: "flex",
                  flexDirection: "column",
                }}
                action={
                  <Typography
                    component="button"
                    type="button"
                    onClick={() => navigate("/tracker-submissions")}
                    sx={{
                      border: 0,
                      bgcolor: "transparent",
                      color: "#04abbb",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                      p: 0,
                    }}
                  >
                    View all
                  </Typography>
                }
              >
                {bestPeople.length === 0 ? (
                  <Typography
                    sx={{ py: 3, textAlign: "center", color: MUTED, fontSize: 13 }}
                  >
                    No strong performers yet — more check-ins will fill this list.
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: "auto",
                      pr: 0.5,
                    }}
                  >
                    {bestPeople.map((c, i) => (
                      <PersonRow
                        key={c.key}
                        person={c}
                        rank={i + 1}
                        onOpen={openPerson}
                        variant="leaderboard"
                      />
                    ))}
                  </Box>
                )}
              </Panel>
            </div>
          </div>

          <Box sx={{ mt: 2 }}>
            <Panel
              title="New submissions"
              subtitle="Latest people check-ins across all trackers"
              action={
                <Typography
                  component="button"
                  type="button"
                  onClick={() => navigate("/tracker-submissions")}
                  sx={{
                    border: 0,
                    bgcolor: "transparent",
                    color: "#04abbb",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                    p: 0,
                  }}
                >
                  View all
                </Typography>
              }
            >
              {(recentSubmissions || []).length === 0 ? (
                <Typography sx={{ py: 3, textAlign: "center", color: MUTED, fontSize: 13 }}>
                  No submissions yet.
                </Typography>
              ) : (
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, color: MUTED, fontSize: 11 }}>
                          Client
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: MUTED, fontSize: 11 }}>
                          Tracker
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: MUTED, fontSize: 11 }}>
                          Form
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: MUTED, fontSize: 11 }}>
                          Review
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: MUTED, fontSize: 11 }}>
                          Coach
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: MUTED, fontSize: 11 }}>
                          Submitted
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentSubmissions.map((row) => (
                        <TableRow
                          key={row.id}
                          hover
                          sx={{ cursor: "pointer" }}
                          onClick={() =>
                            navigate(`/tracker-submissions/${row.id}`)
                          }
                        >
                          <TableCell>
                            <Stack direction="row" spacing={1.25} alignItems="center">
                              <Avatar
                                sx={{
                                  width: 30,
                                  height: 30,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  bgcolor:
                                    row.source === SUBMISSION_SOURCES.PUBLIC
                                      ? "#8b5cf6"
                                      : "#04abbb",
                                }}
                              >
                                {getInitials(row.memberName)}
                              </Avatar>
                              <Box>
                                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                                  {row.memberName || "—"}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: MUTED }}>
                                  {getSubmissionSourceLabel(row.source)}
                                  {row.isNew ? " · New" : ""}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                              {row.trackerName || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: 13 }}>
                            {row.formName || "Form"}
                          </TableCell>
                          <TableCell>
                            <Label
                              variant="ghost"
                              color={reviewColor(row.reviewStatus)}
                            >
                              {getSubmissionReviewLabel(row.reviewStatus)}
                            </Label>
                          </TableCell>
                          <TableCell sx={{ fontSize: 13 }}>
                            {row.coachName || "—"}
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: 13 }}>
                              {row.submittedAt
                                ? moment(row.submittedAt).format("DD MMM YYYY")
                                : "—"}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: MUTED }}>
                              {row.submittedAt
                                ? moment(row.submittedAt).fromNow()
                                : ""}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Panel>
          </Box>
        </>
      )}
    </Box>
  );
}
